import React, { useMemo, useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useCanvasStore } from '@/stores/canvasStore';
import { usePartnrStore } from '@/stores/partnrStore';
import { executeCanvasAction, PartnrCanvasAction } from '@/lib/partnrCanvasActions';
import { calculatePlacement } from '@/lib/partnrPlacementUtils';
import { markdownToHtml } from '@/lib/markdownUtils';
import { getItemCluster, SpatialCluster } from '@/lib/spatialClustering';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, 
  Link2, 
  ClipboardList, 
  ArrowLeftRight,
  Maximize2,
  LayoutGrid,
  RefreshCw,
  Search,
  Eye,
  List,
  Minimize2,
  Loader2,
  Layers,
  Lightbulb,
  PenLine,
  GitCompare,
  MessageCircle,
  Send
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { PartnrMark } from '@/components/partnr/kit/PartnrMark';
import { isPartnrV2Enabled } from '@/lib/partnr/flag';
import { usePartnrPanelStore } from '@/stores/partnrPanelStore';

interface PartnrSelectionPillProps {
  zoom: number;
  panX: number;
  panY: number;
  containerRect: DOMRect | null;
}

interface Action {
  id: PartnrCanvasAction;
  label: string;
  icon: React.ReactNode;
}

const iconMap: Record<string, React.ReactNode> = {
  FileText: <FileText className="w-4 h-4" />,
  Link2: <Link2 className="w-4 h-4" />,
  ClipboardList: <ClipboardList className="w-4 h-4" />,
  ArrowLeftRight: <ArrowLeftRight className="w-4 h-4" />,
  Maximize2: <Maximize2 className="w-4 h-4" />,
  LayoutGrid: <LayoutGrid className="w-4 h-4" />,
  RefreshCw: <RefreshCw className="w-4 h-4" />,
  Search: <Search className="w-4 h-4" />,
  Eye: <Eye className="w-4 h-4" />,
  List: <List className="w-4 h-4" />,
  Minimize2: <Minimize2 className="w-4 h-4" />,
  Lightbulb: <Lightbulb className="w-4 h-4" />,
  PenLine: <PenLine className="w-4 h-4" />,
  GitCompare: <GitCompare className="w-4 h-4" />,
};

export function PartnrSelectionPill({ zoom, panX, panY, containerRect }: PartnrSelectionPillProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showMiniChat, setShowMiniChat] = useState(false);
  const [miniChatQuery, setMiniChatQuery] = useState('');
  const pillRef = useRef<HTMLDivElement>(null);
  const miniChatInputRef = useRef<HTMLInputElement>(null);
  const prevSelectionRef = useRef<string>('');

  const { selectedItemIds, items, viewMode, activeTool, isEditingItem, currentBoardId, createItem, spatialClusters } = useCanvasStore();
  const { activeChats, canvasActionLoading, setCanvasActionLoading, createChat } = usePartnrStore();
  // Partnr 2: "Ask Partnr" opens the side panel with the selection as chips
  const [partnrV2] = useState(isPartnrV2Enabled);
  // Open as a bottom sheet (no inset), the panel covers the canvas and the pill
  const panelCoversCanvas = usePartnrPanelStore((s) => s.isOpen && s.rightInset === 0);
  // Docked, the panel takes the right of the screen: the pill stays left of it
  const panelInset = usePartnrPanelStore((s) => s.rightInset);

  const selectedItems = useMemo(
    () => items.filter(i => selectedItemIds.has(i.id)),
    [items, selectedItemIds]
  );

  // Detect if all selected items are in the same cluster
  const selectedCluster = useMemo((): SpatialCluster | null => {
    if (selectedItems.length < 2 || spatialClusters.length === 0) return null;
    
    const clusters = selectedItems.map(item => getItemCluster(item.id, spatialClusters));
    
    // Check if all items are in the same cluster
    if (clusters.every(c => c !== null && c.id === clusters[0]?.id)) {
      return clusters[0];
    }
    return null;
  }, [selectedItems, spatialClusters]);

  // For single selection, detect if item belongs to a cluster
  const singleItemCluster = useMemo((): SpatialCluster | null => {
    if (selectedItems.length !== 1 || spatialClusters.length === 0) return null;
    const cluster = getItemCluster(selectedItems[0].id, spatialClusters);
    if (!cluster || cluster.memberItemIds.length <= 1) return null;
    return cluster;
  }, [selectedItems, spatialClusters]);

  // Visibility conditions
  const shouldShow = useMemo(() => {
    if (selectedItems.length < 1) return false;
    if (viewMode !== 'focus') return false;
    if (activeTool !== 'select') return false;
    if (isEditingItem) return false;
    if (partnrV2 ? panelCoversCanvas : activeChats.length > 0) return false;
    return true;
  }, [selectedItems.length, viewMode, activeTool, isEditingItem, activeChats.length, partnrV2, panelCoversCanvas]);

  // Position calculation - top-right of selection bounding box in VIEWPORT coords
  const pillPosition = useMemo(() => {
    if (selectedItems.length === 0 || !containerRect) return null;

    // Compute bounding box of selected items in canvas coordinates
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const item of selectedItems) {
      minX = Math.min(minX, item.position_x);
      minY = Math.min(minY, item.position_y);
      maxX = Math.max(maxX, item.position_x + item.width);
      maxY = Math.max(maxY, item.position_y + item.height);
    }

    // Position at top-right corner of bounding box
    const screenX = (maxX + panX) * zoom + containerRect.left + 12; // 12px to the right
    const screenY = (minY + panY) * zoom + containerRect.top - 8; // 8px above top edge

    // Clamp to the visible canvas so the pill stays visible: x is its left edge (the
    // motion transform replaces the translate), so a docked panel needs the pill's
    // 40px plus a 12px gap
    const clampedX = Math.max(60, Math.min(screenX, window.innerWidth - (panelInset ? panelInset + 52 : 60)));
    const clampedY = Math.max(60, Math.min(screenY, window.innerHeight - 120));

    return { x: clampedX, y: clampedY };
  }, [selectedItems, panX, panY, zoom, containerRect, panelInset]);

  // Mini chat label based on selection count
  const miniChatLabel = useMemo(() => {
    if (selectedItems.length === 0) return '';
    if (selectedItems.length === 1) {
      return `Ask about this ${selectedItems[0]?.item_type.replace('_', ' ')}`;
    }
    return `Ask about ${selectedItems.length} items`;
  }, [selectedItems]);

  // Action handler - calls AI and places result
  const handleAction = async (actionId: PartnrCanvasAction) => {
    if (canvasActionLoading) return; // Prevent double-clicks
    if (!currentBoardId) {
      toast.error('Navigate to a board first');
      return;
    }
    if (selectedItems.length === 0) {
      toast.error('No items selected');
      return;
    }

    const sourceIds = selectedItems.map(i => i.id);
    setCanvasActionLoading(true, sourceIds);
    setIsMenuOpen(false);

    try {
      const result = await executeCanvasAction(actionId, selectedItems);
      if (!result) return; // Error already toasted inside executeCanvasAction

      // Convert markdown to HTML for text_card body
      const htmlContent = markdownToHtml(result.text);

      // Calculate smart placement position
      const placement = calculatePlacement(
        selectedItems,
        result.config.resultWidth,
        result.config.resultHeight,
        items,
      );

      // Create the new canvas item
      const newItem = await createItem({
        board_id: currentBoardId,
        item_type: result.config.resultItemType,
        position_x: placement.x,
        position_y: placement.y,
        width: result.config.resultWidth,
        height: result.config.resultHeight,
        content: result.config.resultItemType === 'sticky'
          ? { text: result.text, partnr_generated: true }  // Stickies use plain text
          : { body: htmlContent, partnr_generated: true }, // text_card uses HTML
      });

      if (newItem) {
        // Record connection for visual lines
        const { setCanvasActionConnection } = usePartnrStore.getState();
        setCanvasActionConnection({
          sourceItemIds: sourceIds,
          resultItemId: newItem.id,
        });
        
        // Scroll canvas to show the new item if it's partially off-screen
        const { panX: px, panY: py, zoom: z, setPan } = useCanvasStore.getState();
        const viewRight = -px + window.innerWidth / z;
        const viewBottom = -py + window.innerHeight / z;
        const itemRight = placement.x + result.config.resultWidth;
        const itemBottom = placement.y + result.config.resultHeight;

        let newPanX = px, newPanY = py;
        if (itemRight > viewRight - 40) newPanX = px - (itemRight - viewRight + 80);
        if (itemBottom > viewBottom - 40) newPanY = py - (itemBottom - viewBottom + 80);
        if (newPanX !== px || newPanY !== py) setPan(newPanX, newPanY);
      }
    } catch (error) {
      console.error('[PartnrSelectionPill] Action error:', error);
      toast.error('Partnr could not do that. Try again.');
    } finally {
      setCanvasActionLoading(false);
    }
  };

  // Handle mini chat submit
  const handleMiniChatSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!miniChatQuery.trim() || !currentBoardId || selectedItems.length === 0) return;
    
    setShowMiniChat(false);
    const query = miniChatQuery;
    setMiniChatQuery('');
    await createChat(currentBoardId, selectedItems, query);
  };

  // Open full chat with selected items as context, no auto-prompt
  const handleOpenFullChat = async () => {
    if (!currentBoardId || selectedItems.length === 0) return;
    setShowMiniChat(false);
    setMiniChatQuery('');
    await createChat(currentBoardId, selectedItems);
  };

  // Click-outside to close - use setTimeout to avoid catching the opening click
  useEffect(() => {
    if (!isMenuOpen && !showMiniChat) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (pillRef.current && !pillRef.current.contains(e.target as Node)) {
        setIsMenuOpen(false);
        setShowMiniChat(false);
      }
    };

    // Delay attaching the handler so the opening click doesn't trigger it
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen, showMiniChat]);

  // Close menu/chat when selection changes
  useEffect(() => {
    const currentSelection = Array.from(selectedItemIds).sort().join(',');
    if (prevSelectionRef.current && prevSelectionRef.current !== currentSelection) {
      setIsMenuOpen(false);
      setShowMiniChat(false);
      setMiniChatQuery('');
    }
    prevSelectionRef.current = currentSelection;
  }, [selectedItemIds]);

  if (!shouldShow || !pillPosition) return null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        ref={pillRef}
        initial={{ opacity: 0, scale: 0.9, y: -8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: -8 }}
        transition={{ duration: 0.15, ease: 'easeOut' }}
        className="fixed pointer-events-auto"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        style={{
          left: pillPosition.x,
          top: pillPosition.y,
          transform: 'translateX(-50%)',
          zIndex: 9999,
        }}
      >
        {/* Circular sparkle button */}
        <button
          aria-label="Ask Partnr"
          onClick={() => {
            if (canvasActionLoading) return;
            if (partnrV2) {
              usePartnrPanelStore.getState().open({ selectionIds: selectedItems.slice(0, 20).map((i) => i.id) });
              return;
            }
            // Always toggle mini chat for any selection
            setShowMiniChat(!showMiniChat);
            setIsMenuOpen(false);
            setTimeout(() => miniChatInputRef.current?.focus(), 100);
          }}
          disabled={canvasActionLoading}
          className={cn(
            "w-9 h-9 rounded-full flex items-center justify-center",
            "bg-partnr text-partnr-foreground shadow-lg shadow-partnr/30",
            "hover:shadow-xl hover:shadow-partnr/40 hover:scale-110",
            "active:scale-95 transition-all duration-150",
            canvasActionLoading && "opacity-70 pointer-events-none"
          )}
        >
          {canvasActionLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <PartnrMark size={16} />
          )}
        </button>

        {/* Mini chat input for all selections */}
        <AnimatePresence>
          {showMiniChat && selectedItems.length >= 1 && (
            <motion.div
              initial={{ opacity: 0, y: 8, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8, scale: 0.95 }}
              transition={{ duration: 0.12 }}
              className={cn(
                "absolute top-12 right-0",
                "w-[280px] rounded-xl overflow-hidden",
                "bg-background/95 backdrop-blur-md border border-border/50 shadow-xl"
              )}
            >
              <div className="px-3 py-2 border-b border-border/30">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <MessageCircle className="w-3 h-3" />
                  <span>{miniChatLabel}</span>
                </div>
              </div>

              <form onSubmit={handleMiniChatSubmit} className="p-2">
                <input
                  ref={miniChatInputRef}
                  type="text"
                  value={miniChatQuery}
                  onChange={(e) => setMiniChatQuery(e.target.value)}
                  placeholder="Ask Partnr anything..."
                  className="w-full px-3 py-2 text-sm rounded-lg bg-muted/50 border border-border/50 focus:outline-none focus:ring-1 focus:ring-partnr/50 placeholder:text-muted-foreground/50"
                  onMouseDown={(e) => e.stopPropagation()}
                />
                <div className="flex items-center justify-between mt-2 px-1">
                  <button
                    type="button"
                    onClick={handleOpenFullChat}
                    className="text-xs text-partnr hover:text-partnr/80 flex items-center gap-1"
                  >
                    <PartnrMark size={12} />
                    Open full chat
                  </button>
                  <button
                    type="submit"
                    disabled={!miniChatQuery.trim()}
                    className={cn(
                      "text-xs px-2.5 py-1 rounded-md flex items-center gap-1",
                      "bg-partnr text-partnr-foreground hover:bg-partnr/90",
                      "disabled:opacity-50 disabled:pointer-events-none"
                    )}
                  >
                    <Send className="w-3 h-3" />
                    Ask
                  </button>
                </div>
              </form>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}
